import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  get_branch_dashboard_v2,
  get_enrolment_requests,
  get_assessment_bookings,
  set_request_status,
} from "../../api/Dashboard";

// Pastel palette — soft backgrounds with darker accent text.
const PASTEL = {
  pink:   { bg: "#fdf2f8", border: "#fce7f3", accent: "#be185d", icon: "#db2777" },
  blue:   { bg: "#eff6ff", border: "#dbeafe", accent: "#1e40af", icon: "#2563eb" },
  amber:  { bg: "#fffbeb", border: "#fef3c7", accent: "#92400e", icon: "#d97706" },
  green:  { bg: "#ecfdf5", border: "#d1fae5", accent: "#065f46", icon: "#059669" },
  purple: { bg: "#f5f3ff", border: "#ede9fe", accent: "#5b21b6", icon: "#7c3aed" },
  slate:  { bg: "#f8fafc", border: "#e2e8f0", accent: "#334155", icon: "#64748b" },
};

const fmtTime = (t) => {
  if (!t) return "";
  // Accept "HH:MM:SS" or "HH:MM"
  const [hh, mm] = String(t).split(":");
  const h = parseInt(hh, 10);
  if (isNaN(h)) return t;
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${mm || "00"} ${suffix}`;
};

const gradeLabel = (g) => {
  const n = Number(g);
  if (isNaN(n)) return g || "—";
  if (n === 0) return "Kindy";
  // grade in DB starts from 1 = K. shift if needed; safest: just show as Year N
  return `Year ${n}`;
};

const Dashboard = () => {
  const branchName = (() => {
    try {
      const raw = localStorage.getItem("b_name");
      if (!raw) return "your branch";
      return JSON.parse(raw);
    } catch {
      return localStorage.getItem("b_name") || "your branch";
    }
  })();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    (async () => {
      const res = await get_branch_dashboard_v2();
      if (res?.status) setData(res.data);
      setLoading(false);
    })();
    get_enrolment_requests().then((r) => r?.status && setRequests(r.data || []));
    get_assessment_bookings().then((r) => r?.status && setBookings(r.data || []));
  }, []);

  // Only the ones nobody has called yet. Once a branch marks a request
  // contacted it drops off the dashboard — this is a to-do list, not a log.
  const newRequests = requests.filter((r) => r.status === "new");
  const newBookings = bookings.filter((b) => b.status === "new");

  const markDone = async (kind, id, status, setter, list) => {
    const res = await set_request_status(kind, id, status);
    if (res?.status) setter(list.map((x) => (x.id === id ? { ...x, status } : x)));
  };

  const a = data?.attention || {};
  const stats = data?.stats || {};
  const todayClasses = data?.today_classes || [];

  const attentionItems = [
    // Both come off the website and are somebody waiting on a call from this
    // branch, so they sit ahead of the rest.
    //
    // Assessment leads because that is the order a family goes through: the
    // assessment is step one and the enrolment follows it. Listing them the
    // other way round read as though enrolling came first.
    {
      key: "assessments",
      label: "Assessments & consultations",
      sub: "To be booked in",
      count: newBookings.length,
      to: "/branch/dashboard",
      tone: PASTEL.purple,
      icon: "fa-calendar-check",
    },
    {
      key: "enrolments",
      label: "Enrolment requests",
      sub: "From the website",
      count: newRequests.length,
      to: "/branch/dashboard",
      tone: PASTEL.green,
      icon: "fa-file-signature",
    },
    {
      key: "messages",
      label: "Unread messages",
      sub: "Contact",
      count: a.unread_messages || 0,
      to: "/branch/enquiries",
      tone: PASTEL.pink,
      icon: "fa-envelope",
    },
    {
      key: "tutoring",
      label: "Tutoring questions",
      sub: "Awaiting reply",
      count: a.tutoring_open || 0,
      to: "/branch/tutoring",
      tone: PASTEL.blue,
      icon: "fa-circle-question",
    },
    {
      key: "tests",
      label: "Tests this week",
      sub: "Events & Tests",
      count: a.tests_this_week || 0,
      to: "/branch/events/all",
      tone: PASTEL.amber,
      icon: "fa-clipboard-check",
    },
  ];

  const quickActions = [
    { label: "Add Student", to: "/branch/free/member-add", icon: "fa-user-plus" },
    { label: "New Class", to: "/branch/class-management/add", icon: "fa-chalkboard" },
    { label: "Send Update", to: "/branch/enquiries", icon: "fa-bullhorn" },
  ];

  return (
    <div className="right-nav" id="dash-event" style={{ padding: "20px 24px" }}>
      <div style={{ marginBottom: 18 }}>
        <h4 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: "#1a1a2e" }}>
          Welcome back{branchName ? `, ${branchName}` : ""}
        </h4>
        <p style={{ margin: "4px 0 0", fontSize: 12, color: "#64748b" }}>
          {new Date().toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" })}
        </p>
      </div>

      {/* Attention items */}
      <Section title="Needs your attention">
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 12,
        }}>
          {attentionItems.map((item) => (
            <AttentionCard key={item.key} item={item} loading={loading} />
          ))}
        </div>
      </Section>

      {/* Free assessments and the parents who asked to speak to someone. */}
      {newBookings.length > 0 && (
        <Section title="Assessments & consultations">
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
            {newBookings.map((b) => (
              <RequestRow
                key={b.id}
                title={b.student_name}
                meta={[
                  b.year_level ? `Year ${b.year_level}` : null,
                  b.mode === "campus" ? "On campus" : "Online",
                  b.wants_consultation ? "wants to talk to someone" : null,
                ].filter(Boolean).join(" · ")}
                detail={
                  b.mode === "campus"
                    ? [`Prefers ${b.preferred_date}${b.preferred_time ? ` at ${b.preferred_time}` : ""}`]
                    : []
                }
                contact={[b.mobile, b.email].filter(Boolean).join(" · ")}
                amount=""
                onDone={() => markDone("assessment", b.id, "contacted", setBookings, bookings)}
              />
            ))}
          </div>
        </Section>
      )}

      {/* Enrolment requests — nothing is paid or enrolled until this branch
          rings the family and takes payment. */}
      {newRequests.length > 0 && (
        <Section title="Enrolment requests">
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
            {newRequests.map((r) => (
              <RequestRow
                key={r.id}
                title={`${r.first_name || ""} ${r.last_name || ""}`.trim()}
                meta={[
                  r.year_level ? `Year ${r.year_level}` : null,
                  r.term_label,
                  `${r.weeks} ${Number(r.weeks) === 1 ? "lesson" : "lessons"}`,
                ].filter(Boolean).join(" · ")}
                detail={(r.items || []).map((i) =>
                  `${i.course_name} — ${i.class_label || "class to be confirmed"}`
                )}
                contact={[r.parent_name, r.parent_mobile || r.mobile, r.parent_email || r.email]
                  .filter(Boolean).join(" · ")}
                amount={`$${Number(r.total || 0).toFixed(2)}`}
                onDone={() => markDone("enrolment", r.id, "contacted", setRequests, requests)}
              />
            ))}
          </div>
        </Section>
      )}

      {/* Today's classes */}
      <Section title="Today's classes">
        <div style={{
          background: "#fff", border: "1px solid #e5e7eb",
          borderRadius: 10, overflow: "hidden",
        }}>
          {loading ? (
            <div style={{ padding: 20, color: "#94a3b8", fontSize: 13 }}>Loading…</div>
          ) : todayClasses.length === 0 ? (
            <div style={{ padding: 24, color: "#94a3b8", fontSize: 13 }}>
              No classes scheduled for today.
            </div>
          ) : todayClasses.map((c, i) => (
            <ClassRow key={c.id} c={c} first={i === 0} />
          ))}
        </div>
      </Section>

      {/* At a glance */}
      <Section title="At a glance">
        <div style={{
          background: "#fff", border: "1px solid #e5e7eb",
          borderRadius: 10, padding: "16px 20px",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: 16,
        }}>
          <GlanceStat label="Enrolled" value={stats.students_enrolled} sub="students" />
          <GlanceStat label="Free" value={stats.students_free} sub="students" />
          <GlanceStat label="Active" value={stats.active_classes} sub="classes" />
          <GlanceStat label="Active" value={stats.active_courses} sub="courses" />
          <GlanceStat label="Teachers" value={stats.teachers} sub="" />
        </div>
      </Section>

      {/* Quick actions */}
      <Section title="Quick actions">
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 10,
        }}>
          {quickActions.map((qa) => (
            <Link key={qa.label} to={qa.to} style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "12px 16px",
              background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
              fontSize: 13, color: "#1e293b", textDecoration: "none",
              transition: "all 0.15s ease",
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = PASTEL.slate.bg;
                e.currentTarget.style.borderColor = PASTEL.slate.border;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#fff";
                e.currentTarget.style.borderColor = "#e5e7eb";
              }}
            >
              <i className={`fa-solid ${qa.icon}`} style={{ color: PASTEL.slate.icon, fontSize: 14 }} />
              <span style={{ fontWeight: 500 }}>{qa.label}</span>
            </Link>
          ))}
        </div>
      </Section>
    </div>
  );
};

// One waiting family. "Mark contacted" clears it from the dashboard without
// claiming they enrolled — whether they do is settled on the phone.
const RequestRow = ({ title, meta, detail, contact, amount, onDone }) => (
  <div style={{
    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
    gap: 16, padding: "14px 18px", borderTop: "1px solid #f1f5f9",
  }}>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>
        {title || "Unnamed student"}
      </div>
      {meta && (
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{meta}</div>
      )}
      {detail?.map((d, i) => (
        <div key={i} style={{ fontSize: 12, color: "#475569", marginTop: 3 }}>{d}</div>
      ))}
      {contact && (
        <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>{contact}</div>
      )}
    </div>
    <div style={{ textAlign: "right", flexShrink: 0 }}>
      {amount && (
        <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b" }}>{amount}</div>
      )}
      <button
        type="button"
        onClick={onDone}
        style={{
          marginTop: 6, padding: "6px 12px", fontSize: 12, fontWeight: 500,
          border: "1px solid #e5e7eb", borderRadius: 6, background: "#fff",
          color: "#475569", cursor: "pointer", whiteSpace: "nowrap",
        }}
      >
        Mark contacted
      </button>
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 22 }}>
    <h5 style={{
      margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: "#64748b",
      textTransform: "uppercase", letterSpacing: 0.5,
    }}>
      {title}
    </h5>
    {children}
  </div>
);

const AttentionCard = ({ item, loading }) => (
  <Link to={item.to} style={{
    display: "block", textDecoration: "none",
    background: item.tone.bg,
    border: `1px solid ${item.tone.border}`,
    borderRadius: 10, padding: "14px 16px",
    transition: "all 0.15s ease",
  }}
    onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; }}
  >
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, color: item.tone.accent, fontWeight: 600, marginBottom: 4 }}>
          {item.sub}
        </div>
        <div style={{ fontSize: 14, color: "#1f2937", fontWeight: 500 }}>
          {item.label}
        </div>
      </div>
      <div style={{
        flexShrink: 0,
        background: "#fff",
        border: `1px solid ${item.tone.border}`,
        color: item.tone.accent,
        width: 44, height: 44, borderRadius: 10,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 18, fontWeight: 700,
      }}>
        {loading ? "·" : item.count}
      </div>
    </div>
    <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: item.tone.accent }}>
      <i className={`fa-solid ${item.icon}`} style={{ color: item.tone.icon }} />
      <span>Open</span>
      <i className="fa-solid fa-arrow-right" style={{ fontSize: 10 }} />
    </div>
  </Link>
);

const ClassRow = ({ c, first }) => (
  <Link to={`/branch/class-management/edit/${c.id}`} style={{
    display: "grid",
    gridTemplateColumns: "90px 1fr auto auto",
    gap: 14, alignItems: "center",
    padding: "12px 16px",
    borderTop: first ? "none" : "1px solid #f1f5f9",
    background: "#fff", textDecoration: "none", color: "inherit",
    transition: "background 0.15s",
  }}
    onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
    onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
  >
    <span style={{ fontSize: 13, fontWeight: 600, color: PASTEL.blue.accent }}>
      {fmtTime(c.start_time)}
    </span>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {c.class_name}
      </div>
      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
        {c.course_name ? `${c.course_name} · ` : ""}
        {gradeLabel(c.grade)}
        {c.instructor_name ? ` · ${c.instructor_name}` : ""}
      </div>
    </div>
    <span style={{ fontSize: 11, color: "#64748b", padding: "3px 8px", borderRadius: 999, background: "#f1f5f9" }}>
      {c.student_count || 0} student{c.student_count === 1 ? "" : "s"}
    </span>
    <i className="fa-solid fa-chevron-right" style={{ fontSize: 10, color: "#cbd5e1" }} />
  </Link>
);

const GlanceStat = ({ label, value, sub }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
    <span style={{ fontSize: 22, fontWeight: 600, color: "#1f2937" }}>
      {value ?? 0}
    </span>
    <span style={{ fontSize: 11, color: "#64748b" }}>
      {label}{sub ? ` ${sub}` : ""}
    </span>
  </div>
);

export default Dashboard;
